import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../components/layout/Sidebar";
import {
  ArrowLeft,
  Camera,
  KeyRound,
  Shield,
  Siren,
  Eye,
  MonitorSmartphone,
  Lock,
  Wrench,
  Plug,
  HardDrive,
  Cloud,
  Network,
  Phone,
  Mail,
  MapPin,
  CheckCircle,
  AlertTriangle,
  Building,
  FileCheck,
} from "lucide-react";

const SecuritySolutions = () => {
  const [activeTab, setActiveTab] = useState<
    "overview" | "solutions" | "configuration" | "maintenance" | "faq" | "portfolio"
  >("overview");

  const title = "Security Solutions | CCTV, Access Control, Alarms & Remote Monitoring";
  const pageName = "Security Solutions";
  const description =
    "Professional security installations across Kenya: CCTV camera systems, access control, alarms, perimeter security, video analytics, SOC/remote monitoring, and compliance-focused configuration.";
  const canonical = "https://northstart.co.ke/services/security"; // adjust to your live URL

  const heroBadges = [
    { label: "PPRA Aware", icon: <FileCheck className="w-4 h-4 mr-2" /> },
    { label: "Data Protection Ready", icon: <Shield className="w-4 h-4 mr-2" /> },
    { label: "Enterprise-Grade", icon: <Lock className="w-4 h-4 mr-2" /> },
    { label: "Nationwide Install", icon: <Building className="w-4 h-4 mr-2" /> },
  ];

  const solutions = [
    {
      icon: <Camera className="w-8 h-8 text-red-400" />,
      title: "CCTV Camera Systems",
      desc:
        "IP/PoE and hybrid CCTV designs with analytics-ready NVRs. Indoor/outdoor, day/night, WDR, and low-light options.",
      bullets: [
        "IP cameras: 1080p–4K, fixed/varifocal, dome/bullet/turret",
        "NVR sizing for retention (H.265), RAID where required",
        "Event-based recording: motion, line-cross, intrusion",
        "Remote view via secure VPN or cloud relay",
        "ONVIF interoperability & vendor-neutral approach",
      ],
    },
    {
      icon: <KeyRound className="w-8 h-8 text-orange-400" />,
      title: "Access Control Systems",
      desc:
        "Secure access with RFID, PIN, or biometrics. Central controller with per-door policies and visitor management.",
      bullets: [
        "Readers: RFID, PIN, fingerprint/face (where appropriate)",
        "Locks: maglocks, strikes (fail-safe/fail-secure as needed)",
        "Roles, schedules, anti-passback, muster reports",
        "Turnstiles, boom gates, elevator control (optional)",
        "Integration with HR/Time & Attendance",
      ],
    },
    {
      icon: <Siren className="w-8 h-8 text-pink-400" />,
      title: "Intrusion & Alarm Systems",
      desc:
        "Multi-zone alarm panels with sensors and sirens, integrated to CCTV for event tagging and verification.",
      bullets: [
        "Door/window contacts, PIR/microwave sensors, glass-break",
        "Silent/duress alarms, panic buttons",
        "SMS/email/app push alerts; SOC escalation rules",
        "Arm/disarm schedules & user PIN roles",
        "Battery backup & tamper detection",
      ],
    },
    {
      icon: <Shield className="w-8 h-8 text-purple-400" />,
      title: "Perimeter Security",
      desc:
        "Layered perimeter solutions: fence sensors, electric fence energizers, beams, and lighting tied to camera events.",
      bullets: [
        "Zoned electric fence with voltage monitoring",
        "IR/laser beams and spotlight triggers",
        "Razor wire add-ons and gate interlocks",
        "Perimeter analytics: line-crossing & loitering",
        "Lightning protection and proper earthing",
      ],
    },
    {
      icon: <Eye className="w-8 h-8 text-cyan-400" />,
      title: "Video Analytics & AI",
      desc:
        "People/vehicle detection, LPR/ANPR, heatmapping, dwell-time, and object left- behind analytics where suitable.",
      bullets: [
        "Basic VMD vs. AI models where high accuracy is needed",
        "Privacy masking & audit logs for KDPA compliance",
        "Edge analytics vs. server-side VMS processing",
        "False alarm reduction tuning & rules testing",
        "Report exports for investigations/HR",
      ],
    },
    {
      icon: <MonitorSmartphone className="w-8 h-8 text-green-400" />,
      title: "Remote Monitoring (SOC)",
      desc:
        "24/7 viewing, alert triage, escalation matrix and incident tickets. Secure mobile and desktop clients.",
      bullets: [
        "SOC playbooks with escalation SLAs",
        "Event correlation across CCTV, access, alarms",
        "Encrypted streaming; role-based viewing",
        "Audit trails and evidence handling (chain-of-custody)",
        "DR failover to cloud recorder (optional)",
      ],
    },
  ];

  const configuration = [
    {
      title: "Site Survey & Risk Assessment",
      icon: <AlertTriangle className="w-6 h-6 text-yellow-400" />,
      points: [
        "Identify assets, threat vectors, and lighting conditions",
        "Camera placement: height, FOV, counter-lighting, cabling path",
        "Door hardware audit: hinge side, door type, emergency egress",
        "Perimeter topology: fence zones, beam lines, gate logic",
      ],
    },
    {
      title: "Network & Power Design",
      icon: <Network className="w-6 h-6 text-blue-400" />,
      points: [
        "PoE budgets, switch count, UPS sizing & surge protection",
        "Segregated VLANs for cameras, controllers, and clients",
        "NAT/VPN strategy—no open ports; least-privilege firewall rules",
        "Cable standards (Cat6/Cat6A), labeling & documentation",
      ],
    },
    {
      title: "Recording & Retention Planning",
      icon: <HardDrive className="w-6 h-6 text-purple-400" />,
      points: [
        "H.265, fps/bitrate per camera profile; motion vs continuous",
        "Retention calculations (days x cams x bitrate → storage TB)",
        "RAID and hot-spare policy for critical sites",
        "Offsite or cloud backup for evidentiary footage",
      ],
    },
    {
      title: "Access Policies & Identity",
      icon: <Lock className="w-6 h-6 text-emerald-400" />,
      points: [
        "RBAC for admins, operators, auditors, and visitors",
        "Time schedules, doors groups, and anti-passback",
        "MFA for admin portals; password rotation policies",
        "HR/Payroll integration for joiners/movers/leavers",
      ],
    },
    {
      title: "Integration & Automations",
      icon: <Cloud className="w-6 h-6 text-sky-400" />,
      points: [
        "Event links: access denied → pop camera; alarm → PTZ preset",
        "Turn on floodlights/sirens on perimeter breach",
        "Send incidents to ticketing/Slack/Email",
        "Webhooks/SDKs with major VMS/VMS-lite systems",
      ],
    },
    {
      title: "Power Continuity & Safety",
      icon: <Plug className="w-6 h-6 text-orange-400" />,
      points: [
        "UPS autonomy targets (CCTV NVR 30–60 min; access control 2–4 hrs)",
        "Battery health checks and scheduled cycles",
        "Earthing/lightning protection for towers and fences",
        "OSHA safety signage and muster planning",
      ],
    },
    {
      title: "Compliance & Data Protection",
      icon: <Shield className="w-6 h-6 text-indigo-400" />,
      points: [
        "Kenya Data Protection Act (KDPA) alignment & signage",
        "Consent, privacy masking, and retention policies",
        "Audit trails, secure export, watermarking",
        "Incident handling & chain-of-custody SOPs",
      ],
    },
    {
      title: "Handover & Documentation",
      icon: <Wrench className="w-6 h-6 text-pink-400" />,
      points: [
        "As-built drawings, IP plans, credentials vaulting",
        "Admin & operator training, runbooks, quick guides",
        "Acceptance testing with pass/fail checklist",
        "Warranty terms and spare strategy",
      ],
    },
  ];

  const maintenance = [
    {
      title: "Preventive Maintenance (Quarterly)",
      points: [
        "Lens cleaning, IR/illumination test, focus & angle revalidation",
        "Firmware updates for cameras, NVRs, controllers",
        "Health checks: disk SMART, PoE ports, alarms supervision",
        "Reports: uptime, false alarms, storage capacity",
      ],
    },
    {
      title: "Corrective Support (SLA)",
      points: [
        "Remote response within 1–2 hours; on-site within 4–24 hours (tiered)",
        "Spare pool & RMA handling; temporary replacements",
        "Incident RCA and prevention recommendations",
      ],
    },
    {
      title: "Optimization & Audits",
      points: [
        "Analytics retuning to reduce false positives",
        "User access reviews and policy refresh",
        "Capacity planning for added cameras/doors",
      ],
    },
  ];

  const gallery = [
    "/images/security/1.jpeg",
    "/images/security/2.jpeg",
    "/images/security/3.jpeg",
    "/images/security/4.jpeg",
  ];

  const tabs: { id: "overview" | "solutions" | "configuration" | "maintenance" | "faq" | "portfolio"; label: string }[]  = [
    { id: "overview", label: "Overview" as const },
    { id: "solutions", label: "Solutions" as const },
    { id: "configuration", label: "Configuration" as const },
    { id: "maintenance", label: "Maintenance" as const },
    { id: "faq", label: "FAQ" as const },
    { id: "portfolio", label: "Portfolio" as const },
  ];

  // --- JSON-LD (Services + FAQ) ---
  const servicesJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    name: "Northstart Systems",
    url: "https://northstart.co.ke",
    areaServed: "Kenya",
    description,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Meru",
      addressCountry: "KE",
    },
    telephone: "+254700000000",
    sameAs: ["https://northstart.co.ke"],
    makesOffer: [
      { "@type": "Service", name: "CCTV Camera Installation" },
      { "@type": "Service", name: "Access Control Systems" },
      { "@type": "Service", name: "Intrusion & Alarm Systems" },
      { "@type": "Service", name: "Perimeter Security" },
      { "@type": "Service", name: "Video Analytics & AI" },
      { "@type": "Service", name: "Remote Monitoring (SOC)" },
    ],
  };

  const faqItems = [
    {
      q: "How many days of CCTV footage can we keep?",
      a: "Typical SMEs retain 14–30 days. It depends on number of cameras, resolution, fps, and whether you record continuously or on motion. We size storage during design.",
    },
    {
      q: "Do I need internet for CCTV?",
      a: "Not for local recording. Internet is only required for remote viewing or offsite/cloud backup. We recommend secure VPN over exposed ports.",
    },
    {
      q: "Can access control work during power outages?",
      a: "Yes. We include UPS and select fail-safe or fail-secure locks based on your safety policy and emergency egress requirements.",
    },
    {
      q: "Can alarms trigger cameras and lights?",
      a: "Absolutely. We integrate alarms to pop relevant cameras, trigger spotlights, and notify the SOC with snapshots or short clips.",
    },
    {
      q: "Are you KDPA compliant?",
      a: "We align designs and SOPs with the Kenya Data Protection Act, including privacy masking, retention rules, consent signage, and secure evidence export.",
    },
  ];

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="pt-20 bg-gradient-to-br from-slate-200 via-blue-900 to-indigo-200 min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta
          name="keywords"
          content="CCTV Kenya, access control Kenya, alarm systems Nairobi, perimeter security, security installation Meru, video analytics, SOC monitoring, NVR, IP cameras"
        />
        {/* Open Graph */}
        <meta property="og:title" content={pageName + " | Northstart Systems"} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="/images/security/1.jpeg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageName + " | Northstart Systems"} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="/images/security/1.jpeg" />
        {/* JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify(servicesJsonLd)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 flex gap-8">
        <Sidebar />

        <main className="flex-1 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="p-8 bg-gradient-to-r from-red-500/20 to-orange-500/20 border-b border-white/20">
            <div className="flex items-center justify-between mb-6">
              <Link to="/services" className="inline-flex items-center text-cyan-300 hover:text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Services
              </Link>
              <nav className="hidden md:block text-sm text-slate-300">
                <span className="opacity-80">Home</span> / <span className="opacity-80">Services</span> /{" "}
                <span className="text-white">{pageName}</span>
              </nav>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white leading-tight">
              Security Solutions for Businesses in Kenya
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-6 max-w-4xl">{description}</p>

            <div className="flex flex-wrap gap-3">
              {heroBadges.map((b, i) => (
                <div key={i} className="flex items-center bg-white/10 px-3 py-2 rounded-full border border-white/20">
                  {b.icon}
                  <span className="text-slate-100 text-sm">{b.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tabs */}
          <div className="px-6 py-3 bg-white/5 border-b border-white/10 flex flex-wrap gap-2">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-5 py-2.5 rounded-xl text-sm font-medium transition-all ${
                  activeTab === t.id
                    ? "bg-red-500/30 text-white border border-red-400/50"
                    : "bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="p-8 text-white">
            {activeTab === "overview" && (
              <div className="space-y-12">
                <section>
                  <h2 className="text-3xl font-bold mb-6 text-center">What We Deliver</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {solutions.slice(0, 6).map((s, i) => (
                      <div
                        key={i}
                        className="bg-white/5 rounded-2xl border border-white/10 p-6 hover:bg-white/10 transition-all"
                      >
                        <div className="mb-4">{s.icon}</div>
                        <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                        <p className="text-slate-300 text-sm">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="grid md:grid-cols-3 gap-6">
                  <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-6">
                    <h4 className="font-semibold mb-2">Designed for Your Site</h4>
                    <p className="text-slate-300 text-sm">
                      Each installation starts with a detailed survey and risk assessment to ensure coverage and evidence
                      quality.
                    </p>
                  </div>
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-6">
                    <h4 className="font-semibold mb-2">Secure by Default</h4>
                    <p className="text-slate-300 text-sm">
                      We segment networks, harden devices, and prefer VPN access—no exposed ports.
                    </p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-2xl p-6">
                    <h4 className="font-semibold mb-2">Compliant & Documented</h4>
                    <p className="text-slate-300 text-sm">
                      KDPA-aligned retention, masking, and audit trails. Full handover docs and training.
                    </p>
                  </div>
                </section>
              </div>
            )}

            {activeTab === "solutions" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Core Security Solutions</h2>
                <p className="text-slate-300">
                  From CCTV and access control to alarms and SOC monitoring, we tailor layered protection that matches
                  your risk profile and budget.
                </p>

                <div className="grid gap-8">
                  {solutions.map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <div className="flex items-start gap-6">
                        <div className="p-4 bg-white/10 rounded-2xl">{s.icon}</div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-semibold mb-2">{s.title}</h3>
                          <p className="text-slate-300 mb-4">{s.desc}</p>
                          <ul className="grid md:grid-cols-2 gap-2">
                            {s.bullets.map((b, idx) => (
                              <li key={idx} className="flex items-start">
                                <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                                <span className="text-slate-200 text-sm">{b}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "configuration" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Installation & Configuration</h2>
                <p className="text-slate-300">
                  We engineer end-to-end: survey, network and power design, recording/retention planning, policy setup,
                  and integrations—with proper documentation and training.
                </p>

                <div className="grid gap-8">
                  {configuration.map((c, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-8">
                      <div className="flex items-start gap-4 mb-4">
                        {c.icon}
                        <h3 className="text-xl font-semibold">{c.title}</h3>
                      </div>
                      <ul className="grid md:grid-cols-2 gap-2">
                        {c.points.map((p, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                            <span className="text-slate-200 text-sm">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Quick spec tips */}
                <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl border border-red-500/20 p-8">
                  <h3 className="text-2xl font-semibold mb-4 text-center">Quick Spec Tips</h3>
                  <div className="grid md:grid-cols-3 gap-6 text-sm text-slate-200">
                    <div>
                      <h4 className="font-semibold mb-2">CCTV</h4>
                      <p>
                        Balance resolution and fps with storage. Use WDR for backlit areas; choose varifocal for flexible
                        FOV; prefer PoE with documented power budget.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Access Control</h4>
                      <p>
                        Decide fail-safe vs fail-secure per door. Map emergency egress. Apply roles, time schedules, and
                        keep visitor logs.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Alarms & Perimeter</h4>
                      <p>
                        Zone beams and fence lines for accurate localization. Link alarms to cameras and lights to verify
                        events quickly.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "maintenance" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Maintenance, SLAs & Support</h2>
                <p className="text-slate-300">
                  Keep systems healthy and compliant with preventive checks, rapid response SLAs, and ongoing
                  optimization.
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                  {maintenance.map((m, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                      <h3 className="text-xl font-semibold mb-3">{m.title}</h3>
                      <ul className="space-y-2 text-sm">
                        {m.points.map((p, idx) => (
                          <li key={idx} className="flex items-start">
                            <CheckCircle className="w-4 h-4 text-emerald-400 mr-2 mt-0.5" />
                            <span className="text-slate-200">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
                  <h3 className="text-lg font-semibold mb-2">Service Areas</h3>
                  <p className="text-slate-300 text-sm">
                    Nairobi, Meru, Mount Kenya region, and nationwide projects on request.
                  </p>
                </div>
              </div>
            )}

            {activeTab === "faq" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Frequently Asked Questions</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {faqItems.map((f, i) => (
                    <div key={i} className="bg-white/5 rounded-2xl border border-white/10 p-6">
                      <h3 className="text-lg font-semibold mb-2">{f.q}</h3>
                      <p className="text-slate-300 text-sm">{f.a}</p>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 rounded-2xl border border-emerald-500/20 p-8 text-center">
                  <h3 className="text-2xl font-semibold mb-3">Need a site survey or quick quote?</h3>
                  <p className="text-slate-300 mb-6">
                    We’ll assess your site and provide a tailored design, cost, and timeline.
                  </p>
                  <div className="flex flex-wrap justify-center gap-4">
                    <a
                      href="tel:+254700000000"
                      className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all inline-flex items-center"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call Us
                    </a>
                    <a
                      href="mailto:procurement@northstart.co.ke"
                      className="bg-white/10 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all border border-white/20 inline-flex items-center"
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Request Quote
                    </a>
                    <Link
                      to="/downloads/northstart-security-brochure.pdf"
                      className="bg-transparent text-emerald-300 px-6 py-3 rounded-xl font-semibold hover:bg-emerald-500/10 transition-all border border-emerald-500/30 inline-flex items-center"
                    >
                      <FileCheck className="w-5 h-5 mr-2" />
                      Download Brochure
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "portfolio" && (
              <div className="space-y-8">
                <h2 className="text-3xl font-bold">Recent Work & Installs</h2>
                <p className="text-slate-300">
                  A few snapshots from deployments. We’ll add more case studies here.
                </p>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {gallery.map((img, idx) => (
                    <div key={idx} className="group relative overflow-hidden rounded-2xl">
                      <img
                        src={img}
                        alt={`Security solution ${idx + 1}`}
                        className="w-full h-64 object-cover transition-transform group-hover:scale-110"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="text-white font-semibold mb-1">Deployment {idx + 1}</h3>
                          <p className="text-slate-200 text-sm">CCTV/Access/Alarms integration</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact strip */}
                <div className="bg-white/5 rounded-2xl border border-white/10 p-6 grid sm:grid-cols-3 gap-6 text-center">
                  <div className="flex flex-col items-center">
                    <Phone className="w-7 h-7 text-cyan-400 mb-2" />
                    <p className="text-slate-200">+254 700 041385</p>
                    <p className="text-slate-400 text-xs">24/7 support line</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <Mail className="w-7 h-7 text-emerald-400 mb-2" />
                    <p className="text-slate-200">info@northstartsystems.com</p>
                    <p className="text-slate-400 text-xs">Replies within 2 hours</p>
                  </div>
                  <div className="flex flex-col items-center">
                    <MapPin className="w-7 h-7 text-purple-400 mb-2" />
                    <p className="text-slate-200">Riara Valley Garden, Apartment C8, 3rd Floor, Off Riara Road Kilimani, Nairobi, Kenya</p>
                    <p className="text-slate-400 text-xs">Nairobi & Nationwide</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default SecuritySolutions;
